import React from "react";

import { Button } from "../components/ui/button";
import { Text } from "../components/ui/text";

import { formatClaimAmount } from "./claimAmounts";
import type { ClaimApi } from "./claimTypes";
import { useClaimFlow } from "./useClaimFlow";

export function ClaimPanel({ api }: { api: ClaimApi }): React.ReactElement {
    const flow = useClaimFlow(api);
    const pending = flow.request?.attempts.some(
        (a) => !["paid", "expired_unexecuted", "superseded"].includes(a.state)
    );
    return (
        <section
            className="flex flex-col gap-4 py-6 border-b border-line-base"
            aria-label="Claim rewards"
            aria-busy={flow.busy}
        >
            <Text variant="body">Claim rewards</Text>
            <Text variant="small">
                Rewards become available after each distribution. Unclaimed
                rewards do not expire.
            </Text>
            {!flow.authorized ? (
                <Button
                    size="lg"
                    disabled={flow.busy || !flow.ready}
                    onClick={() => void flow.load()}
                >
                    View claimable rewards
                </Button>
            ) : (
                <>
                    {flow.rewards?.availabilityStatus !== "fresh" && (
                        <Text variant="small">
                            Balances could not be refreshed. The last available
                            amounts may have changed.
                        </Text>
                    )}
                    {flow.rewards?.claimable.map((total) => (
                        <div key={`${total.network}/${total.assetMint}`}>
                            <Text variant="number">
                                {formatClaimAmount(
                                    total.amountAtomic,
                                    total.payoutAssetDecimals
                                )}
                            </Text>
                            <Text variant="small" className="break-all">
                                {total.assetMint} ({total.network})
                            </Text>
                        </div>
                    ))}
                    {flow.rewards?.availabilityStatus === "fresh" &&
                        !flow.rewards.claimable.length &&
                        !pending && (
                            <Text variant="small">
                                No rewards are available to claim yet.
                            </Text>
                        )}
                    <div className="flex flex-wrap gap-3">
                        <Button
                            size="lg"
                            disabled={
                                flow.busy ||
                                !flow.ready ||
                                (!flow.rewards?.canClaim && !pending)
                            }
                            onClick={() => void flow.prepare()}
                        >
                            {pending ? "Review pending claim" : "Claim all"}
                        </Button>
                        <Button
                            variant="transparent"
                            disabled={flow.busy}
                            onClick={() => void flow.load()}
                        >
                            Refresh
                        </Button>
                    </div>
                </>
            )}
            {flow.review && (
                <div
                    className="flex flex-col gap-4 border-t border-line-base pt-4"
                    aria-label="Review claim"
                >
                    <Text variant="body">Review your claim</Text>
                    <Text variant="small">
                        Your wallet pays the network fee and any account
                        creation costs in SOL. Each payment needs a transaction
                        signature.
                    </Text>
                    {flow.review.request.attempts
                        .filter((a) => a.requiredAction === "sign_and_submit")
                        .map((a) => (
                            <div
                                key={a.attemptUuid}
                                className="flex flex-col gap-2"
                            >
                                <Text variant="number">
                                    {formatClaimAmount(
                                        a.amountBaseUnits,
                                        a.payoutAssetDecimals
                                    )}
                                </Text>
                                <Text variant="small" className="break-all">
                                    Token: {a.assetMint} ({a.network})
                                </Text>
                                <Text variant="small" className="break-all">
                                    Recipient: {a.beneficiaryAddress}
                                </Text>
                                <Text variant="small" className="break-all">
                                    Fee payer: {a.feePayerAddress}
                                </Text>
                                <Text variant="number">
                                    Estimated cost:{" "}
                                    {formatClaimAmount(
                                        flow.review!.fees[a.attemptUuid] ?? "0",
                                        9
                                    )}{" "}
                                    SOL
                                </Text>
                            </div>
                        ))}
                    <Button
                        size="lg"
                        disabled={
                            flow.busy ||
                            !flow.review.request.attempts.some(
                                (a) => a.requiredAction === "sign_and_submit"
                            )
                        }
                        onClick={() => void flow.submit()}
                    >
                        Sign and claim
                    </Button>
                </div>
            )}
            {flow.request && (
                <div
                    role="status"
                    aria-live="polite"
                    className="flex flex-col gap-2"
                >
                    {flow.request.attempts.map((a, i) => (
                        <Text key={a.attemptUuid} variant="small">
                            Payment {i + 1}:{" "}
                            {a.state === "paid"
                                ? "Completed"
                                : a.state === "superseded"
                                  ? "Replaced by a newer claim review"
                                  : a.state === "expired_unexecuted"
                                    ? "This transaction expired without completing. Refresh rewards for the latest status."
                                    : a.state === "conflict"
                                      ? "Needs payment review"
                                      : a.transactionSignature
                                        ? "Confirming payment"
                                        : "Awaiting signature or payment confirmation"}
                        </Text>
                    ))}
                </div>
            )}
            {flow.error && (
                <Text variant="small" role="alert">
                    {flow.error}
                </Text>
            )}
            {!flow.ready && (
                <Text variant="small">
                    Connect the wallet that will pay the network fee.
                </Text>
            )}
        </section>
    );
}
